// src/private/src/pages/app/reports/DailyPulse.jsx
import * as React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  TableSortLabel,
  FormControl,
  FormControlLabel,
  InputLabel,
  Button,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tooltip,
  TextField,
  Skeleton,
  IconButton,
  Popover,
  InputAdornment,
} from '@mui/material';
import {
  Refresh,
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Close as CloseIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { LinkSimple } from '@phosphor-icons/react';

import {
  DateRangePicker,
  DollarFormatter,
  PercentageFormatter
} from '@src/components';
import { useDailyPulse } from '@src/context';
import { RouterLink } from '@src/components/core/link';
import { CLIENT_STATUS_OPTIONS, DEFAULT_CLIENT_STATUS_FILTER } from '@src/constants/clients';
import api from '@src/api/clients';

const MULTI_SELECT_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 48 * 4.5 + 8,
      width: 280,
    },
  },
};

const STATUS_LABEL_LOOKUP = CLIENT_STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const PRIORITY_CLIENT_STATUSES = ['onboarding', 'active_new'];
const PRIORITY_CLIENT_STATUS_SET = new Set(PRIORITY_CLIENT_STATUSES);
const PURPLE_STATUS_COLOR = '#7c4dff';
const PURPLE_STATUS_COLOR_DARK = '#c38aff';

const formatLaunchDate = (value) => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format('MM/DD');
};

const getClientLaunchDateLabel = (status, launchDate) => {
  if (!PRIORITY_CLIENT_STATUS_SET.has(status)) {
    return null;
  }
  return formatLaunchDate(launchDate);
};

const toFiniteNumber = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const safeRatio = (numerator, ...denominatorParts) => {
  const num = Number(numerator);
  if (!Number.isFinite(num)) {
    return null;
  }

  let denominator = 0;
  for (const part of denominatorParts) {
    const value = Number(part);
    if (!Number.isFinite(value)) {
      return null;
    }
    denominator += value;
  }

  if (!Number.isFinite(denominator) || denominator === 0) {
    return null;
  }

  const ratio = num / denominator;
  return Number.isFinite(ratio) ? ratio : null;
};

const safeAlpha = (color, opacity) => {
  if (typeof alpha === 'function' && typeof color === 'string') {
    try {
      return alpha(color, opacity);
    } catch {
      // If alpha application fails fall back to the base color.
    }
  }

  return color;
};

const toPositiveNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const shouldDisplayClientRow = (row) => {
  if (!row) {
    return false;
  }

  if (toPositiveNumber(row.total_spend_sum ?? row.total_spend)) {
    return true;
  }

  if (toPositiveNumber(row.budget_amount ?? row.budget)) {
    return true;
  }

  if (toPositiveNumber(row.facebook_daily_budget_total ?? row.facebook_budget_amount)) {
    return true;
  }

  return false;
};

const parseRatioValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getMarketingSeverity = (value, goal) => {
  const metric = parseRatioValue(value);
  const target = parseRatioValue(goal);
  if (metric === null || target === null) {
    return null;
  }

  if (metric <= target) {
    return 'low';
  }

  const mediumThreshold = target * 1.15;
  if (metric <= mediumThreshold) {
    return 'medium';
  }
  return 'high';
};

const marketingSeverityStyles = {
  low: (theme) => ({
    color: theme.palette.success.dark,
    backgroundColor: safeAlpha(theme.palette.success.light || theme.palette.success.main, 0.35),
    borderColor: safeAlpha(theme.palette.success.main, 0.5),
  }),
  medium: (theme) => ({
    color: theme.palette.warning.dark || theme.palette.warning.main,
    backgroundColor: safeAlpha(theme.palette.warning.light || theme.palette.warning.main, 0.35),
    borderColor: safeAlpha(theme.palette.warning.main, 0.45),
  }),
  high: (theme) => ({
    color: theme.palette.error.dark,
    backgroundColor: safeAlpha(theme.palette.error.light || theme.palette.error.main, 0.35),
    borderColor: safeAlpha(theme.palette.error.main, 0.45),
  }),
};

const CostOfMarketingPill = ({ value, goal }) => {
  const severity = getMarketingSeverity(value, goal);
  const display = <PercentageFormatter value={value} />;

  if (!severity) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(8),
        ...marketingSeverityStyles[severity](theme),
      })}
    >
      {display}
    </Box>
  );
};

const INGESTION_STATUS_EMOJI = {
  ok: '✅',
  warning: '⚠️',
  critical: '🔴',
  missing: '❌',
};

const formatRuntimeSeconds = (value) => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  if (seconds < 1) {
    return '<1s';
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds - minutes * 60);
  if (minutes && remainingSeconds) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  if (minutes) {
    return `${minutes}m`;
  }
  return `${remainingSeconds}s`;
};

const isTaskFreshInLocalTimezone = (task) => {
  if (!task || task.status === 'missing') {
    return false;
  }

  // backend_data is informational only now — the latest lead date is shown for
  // context but no longer drives health (a quiet sales week is not a broken
  // feed). Never treat it as "stale".
  if (task.name === 'backend_data') {
    return true;
  }

  if (task.finished_at) {
    const finishedAt = dayjs(task.finished_at);
    if (finishedAt.isValid()) {
      return dayjs().diff(finishedAt, 'hour', true) <= 24;
    }
  }

  return Boolean(task.fresh);
};

const buildTaskStatusText = (task) => {
  const parts = [];
  const statusLabel = (task?.status || 'missing').replace(/_/g, ' ');
  parts.push(statusLabel);

  const isFresh = isTaskFreshInLocalTimezone(task);
  if (task && !isFresh && task.status !== 'missing') {
    parts.push('stale');
  }

  const runtime = formatRuntimeSeconds(task?.runtime_seconds);
  if (runtime) {
    parts.push(runtime);
  }

  if (task?.finished_at) {
    parts.push(dayjs(task.finished_at).fromNow());
  } else if (task?.status !== 'missing') {
    parts.push('no recent run');
  }

  if (task?.max_created_date) {
    parts.push(`last lead ${dayjs(task.max_created_date).format('YYYY-MM-DD')}`);
  }

  return parts.join(' • ');
};

const isHealthyIngestionTask = (task) => {
  if (!task || task.status === 'missing') {
    return false;
  }
  if (task.name === 'backend_data') {
    return isTaskFreshInLocalTimezone(task);
  }
  return (
    String(task.status).toLowerCase() === 'succeeded' &&
    isTaskFreshInLocalTimezone(task)
  );
};

const getIngestionStatusKey = (ingestionHealth) => {
  if (!ingestionHealth) {
    return null;
  }
  // Prefer the server-computed tier (ok / warning / critical / missing): it
  // folds in the lead-flow loop's backend-feed anomaly + severity, so the flag
  // agrees with the alerts/Slack and isn't recomputed from lead-recency.
  if (ingestionHealth.level) {
    return ingestionHealth.level;
  }
  // Legacy fallback for cached payloads without `level`.
  if (!ingestionHealth.has_mapping) {
    return 'missing';
  }
  const tasks = Array.isArray(ingestionHealth.tasks) ? ingestionHealth.tasks : [];
  return tasks.length > 0 && tasks.every(isHealthyIngestionTask)
    ? 'ok'
    : 'warning';
};

const IngestionStatusIndicator = ({ ingestionHealth, clientId }) => {
  if (!ingestionHealth) {
    return null;
  }

  const tasks = Array.isArray(ingestionHealth.tasks) ? ingestionHealth.tasks : [];
  const statusKey = getIngestionStatusKey(ingestionHealth);
  const symbol = INGESTION_STATUS_EMOJI[statusKey] || '';

  // When we know the client, the flag deep-links straight to that client's
  // backend mapping (Data Integrations, pre-filtered) so you can jump from
  // "why is this yellow?" to the mapping in one click.
  const mappingHref = clientId ? `/data-integrations?client_id=${clientId}` : null;

  const feedAlert = ingestionHealth.feed_alert;
  const tooltipContent = (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" mb={0.5}>
        Data tasks
      </Typography>
      {feedAlert?.reason ? (
        <Typography
          variant="body2"
          color={statusKey === 'critical' ? 'error.main' : 'warning.main'}
          mb={0.5}
        >
          {feedAlert.reason}
        </Typography>
      ) : null}
      {!ingestionHealth.has_mapping ? (
        <Typography variant="body2" color="text.secondary">
          No email, API, SFTP, or Google Sheet mappings configured.
        </Typography>
      ) : tasks.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No recent task runs recorded.
        </Typography>
      ) : (
        <Stack spacing={0.5}>
          {tasks.map((task, index) => (
            <Box key={`${task?.name || 'task'}-${index}`}>
              <Typography variant="body2" fontWeight={600}>
                {task?.name || 'Task'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {buildTaskStatusText(task)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
      {mappingHref ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Click to open backend mapping →
        </Typography>
      ) : null}
    </Box>
  );

  if (!symbol) {
    return null;
  }

  const indicatorSx = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '1.05rem',
    ml: 0.5,
    cursor: mappingHref ? 'pointer' : 'default',
    textDecoration: 'none',
    lineHeight: 1,
  };

  const indicator = mappingHref ? (
    <Box
      component={RouterLink}
      href={mappingHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={indicatorSx}
    >
      {symbol}
    </Box>
  ) : (
    <Box component="span" sx={indicatorSx}>
      {symbol}
    </Box>
  );

  return (
    <Tooltip arrow placement="top" title={tooltipContent}>
      {indicator}
    </Tooltip>
  );
};

// Separate, softer flag from the feed triangle: the lead-flow loop found this
// client's lead VOLUME down vs its OWN same-weekday baseline. A quiet pipeline,
// not a broken one — so it's its own indicator (📉), never the data-task flag.
const VolumeStatusIndicator = ({ ingestionHealth }) => {
  const alert = ingestionHealth?.volume_alert;
  if (!alert) {
    return null;
  }
  const tooltipContent = (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" mb={0.5}>
        Lead volume down vs baseline
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {alert.reason}
      </Typography>
    </Box>
  );
  return (
    <Tooltip arrow placement="top" title={tooltipContent}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: '1.05rem',
          ml: 0.5,
          cursor: 'default',
        }}
      >
        📉
      </Box>
    </Tooltip>
  );
};

const ParentIngestionIndicator = ({ childrenRows }) => {
  const rows = (Array.isArray(childrenRows) ? childrenRows : []).filter(
    (row) => row?.ingestion_health
  );
  if (rows.length === 0) {
    return null;
  }

  const childStatuses = rows.map((row) => ({
    name: row.client__name || 'Client',
    statusKey: getIngestionStatusKey(row.ingestion_health),
  }));
  const hasCritical = childStatuses.some((c) => c.statusKey === 'critical');
  const hasWarning = childStatuses.some((c) => c.statusKey === 'warning');
  const hasOk = childStatuses.some((c) => c.statusKey === 'ok');
  const statusKey = hasCritical
    ? 'critical'
    : hasWarning
    ? 'warning'
    : hasOk
    ? 'ok'
    : 'missing';
  const symbol = INGESTION_STATUS_EMOJI[statusKey] || '';

  if (!symbol) {
    return null;
  }

  const tooltipContent = (
    <Box sx={{ maxWidth: 320 }}>
      <Typography variant="subtitle2" mb={0.5}>
        Data status by client
      </Typography>
      <Stack spacing={0.25}>
        {childStatuses.map((child, index) => (
          <Typography key={`${child.name}-${index}`} variant="caption" color="text.secondary">
            {INGESTION_STATUS_EMOJI[child.statusKey] || ''} {child.name}
          </Typography>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Tooltip arrow placement="top" title={tooltipContent}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          fontSize: '1.05rem',
          ml: 0.5,
          cursor: 'default',
        }}
      >
        {symbol}
      </Box>
    </Tooltip>
  );
};

const HOLD_TIER_META = {
  slow_down: { label: 'Slowed', kind: 'throttle' },
  minimal_spend: { label: 'Minimal spend', kind: 'throttle' },
  boost: { label: 'Boosted', kind: 'boost' },
};

const getActiveHolds = (row) =>
  Array.isArray(row?.active_holds) ? row.active_holds : [];

// Open non-payment pause (campaigns switched off because an invoice went past
// due). Attached server-side from InvoicePastDuePause; null once payment posts.
const getPastDuePause = (row) =>
  row?.past_due_pause && typeof row.past_due_pause === 'object'
    ? row.past_due_pause
    : null;

const isPastDuePaused = (row) => Boolean(getPastDuePause(row));

const hasThrottleHold = (row) =>
  getActiveHolds(row).some(
    (hold) => (HOLD_TIER_META[hold.tier]?.kind || 'throttle') === 'throttle'
  );

// Heuristic for pauses nobody recorded: budget set, spent earlier in the
// period, but $0 yesterday — campaigns look switched off.
const isSpendOff = (row) => {
  if (hasThrottleHold(row)) {
    return false;
  }
  const budget = toFiniteNumber(row?.budget_amount);
  const spend = toFiniteNumber(row?.total_spend_sum);
  const yesterday = toFiniteNumber(row?.yesterday_spend);
  return (
    budget !== null && budget > 0 &&
    spend !== null && spend > 0 &&
    yesterday === 0
  );
};

const isHeldOrOff = (row) =>
  hasThrottleHold(row) || isPastDuePaused(row) || isSpendOff(row);

const formatHoldDate = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM D') : String(value || '');
};

const HOLD_PILL_VARIANTS = {
  boost: (theme) => ({
    color: theme.palette.success.dark,
    backgroundColor: safeAlpha(theme.palette.success.main, 0.12),
    borderColor: safeAlpha(theme.palette.success.main, 0.4),
  }),
  // Non-payment pause: red so it reads as a hard stop, not a soft throttle.
  pastdue: (theme) => ({
    color: theme.palette.error.dark,
    backgroundColor: safeAlpha(theme.palette.error.main, 0.12),
    borderColor: safeAlpha(theme.palette.error.main, 0.45),
  }),
  throttle: (theme) => ({
    color: theme.palette.text.secondary,
    backgroundColor: safeAlpha(theme.palette.text.primary, 0.05),
    borderColor: theme.palette.divider,
  }),
};

const holdPillSx = (kind) => (theme) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontWeight: 600,
  fontSize: '0.68rem',
  px: 0.6,
  py: 0.1,
  borderRadius: 1,
  border: '1px solid',
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  cursor: 'default',
  ...(HOLD_PILL_VARIANTS[kind] || HOLD_PILL_VARIANTS.throttle)(theme),
});

const pastDuePillMoney = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? `$${Math.round(num).toLocaleString()}` : null;
};

const HoldBadge = ({ holds }) => {
  if (!Array.isArray(holds) || holds.length === 0) {
    return null;
  }
  const throttles = holds.filter(
    (hold) => (HOLD_TIER_META[hold.tier]?.kind || 'throttle') === 'throttle'
  );
  const primary = throttles[0] || holds[0];
  const meta = HOLD_TIER_META[primary.tier] || { label: primary.tier, kind: 'throttle' };

  const tooltip = (
    <Stack spacing={0.5}>
      {holds.map((hold, index) => (
        <Box key={`${hold.tier}-${index}`}>
          <Typography variant="caption" fontWeight={600} display="block">
            {(HOLD_TIER_META[hold.tier]?.label) || hold.tier}
            {' — '}
            {hold.scope_type === 'overall'
              ? 'whole account'
              : hold.scope_label || hold.scope_type}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {formatHoldDate(hold.start_date)} → {formatHoldDate(hold.end_date)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Tooltip arrow placement="top" title={tooltip}>
      <Box component="span" sx={holdPillSx(meta.kind)}>
        {meta.kind === 'boost' ? '⏵' : '⏸'} {meta.label} → {formatHoldDate(primary.end_date)}
      </Box>
    </Tooltip>
  );
};

const SpendOffBadge = () => (
  <Tooltip
    arrow
    placement="top"
    title="Budget is set but spend was $0 yesterday — campaigns look switched off. If this is intentional, log a hold on Pacing & Adjustments so it's flagged here."
  >
    <Box component="span" sx={holdPillSx('throttle')}>
      ⏸ Spend off
    </Box>
  </Tooltip>
);

const PastDuePauseBadge = ({ pause }) => {
  if (!pause) {
    return null;
  }
  const amount = pastDuePillMoney(pause.invoice_amount);
  const pausedLabel = pause.paused_at ? formatHoldDate(pause.paused_at) : null;
  const invoiceLine = [
    pause.invoice_number ? `Invoice ${pause.invoice_number}` : null,
    amount,
  ]
    .filter(Boolean)
    .join(' · ');

  const tooltip = (
    <Box sx={{ maxWidth: 280 }}>
      <Typography variant="subtitle2" mb={0.5}>
        Paused — invoice unpaid
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Campaigns were auto-paused after the invoice went past due. They turn
        back on automatically once payment posts.
      </Typography>
      {invoiceLine ? (
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
          {invoiceLine}
        </Typography>
      ) : null}
      {pausedLabel ? (
        <Typography variant="caption" color="text.secondary" display="block">
          Paused {pausedLabel}
          {pause.days_past_due != null
            ? ` · ${pause.days_past_due} days past due`
            : ''}
        </Typography>
      ) : null}
      {pause.count > 1 ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {pause.count} unpaid invoices
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <Tooltip arrow placement="top" title={tooltip}>
      <Box component="span" sx={holdPillSx('pastdue')}>
        ⏸ Unpaid
      </Box>
    </Tooltip>
  );
};

const ParentHoldIndicator = ({ childrenRows }) => {
  const rows = Array.isArray(childrenRows) ? childrenRows : [];
  const flagged = rows.filter((row) => isHeldOrOff(row));
  if (flagged.length === 0) {
    return null;
  }

  const tooltip = (
    <Stack spacing={0.25}>
      {flagged.map((row, index) => {
        const holds = getActiveHolds(row).filter(
          (hold) => (HOLD_TIER_META[hold.tier]?.kind || 'throttle') === 'throttle'
        );
        const state = isPastDuePaused(row)
          ? 'Paused — unpaid invoice'
          : holds.length > 0
          ? `${(HOLD_TIER_META[holds[0].tier]?.label) || holds[0].tier} → ${formatHoldDate(holds[0].end_date)}`
          : 'Spend off — $0 yesterday';
        return (
          <Typography key={`${row.client_id}-${index}`} variant="caption" color="text.secondary">
            ⏸ {row.client__name}: {state}
          </Typography>
        );
      })}
    </Stack>
  );

  return (
    <Tooltip arrow placement="top" title={tooltip}>
      <Box component="span" sx={holdPillSx('throttle')}>
        ⏸ {flagged.length}
      </Box>
    </Tooltip>
  );
};

const parsePercentageValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const getPacingSeverity = (value) => {
  const metric = parsePercentageValue(value);
  if (metric === null) {
    return null;
  }
  if (metric < 70) return 'critical_under';
  if (metric < 90) return 'watch_under';
  if (metric <= 110) return 'healthy';
  return 'critical_over';
};

const pacingSeverityStyles = {
  critical_under: () => ({
    color: '#6d28d9',
    backgroundColor: safeAlpha('#a78bfa', 0.35),
    borderColor: safeAlpha('#8b5cf6', 0.5),
  }),
  watch_under: (theme) => ({
    color: theme.palette.warning.dark || theme.palette.warning.main,
    backgroundColor: safeAlpha(theme.palette.warning.light || theme.palette.warning.main, 0.25),
    borderColor: safeAlpha(theme.palette.warning.main, 0.45),
  }),
  healthy: marketingSeverityStyles.low,
  watch_over: (theme) => ({
    color: theme.palette.warning.dark || theme.palette.warning.main,
    backgroundColor: safeAlpha(theme.palette.warning.light || theme.palette.warning.main, 0.25),
    borderColor: safeAlpha(theme.palette.warning.main, 0.45),
  }),
  critical_over: (theme) => ({
    color: theme.palette.error.dark,
    backgroundColor: safeAlpha(theme.palette.error.light || theme.palette.error.main, 0.35),
    borderColor: safeAlpha(theme.palette.error.main, 0.45),
  }),
};

const PacingPill = ({ value }) => {
  const severity = getPacingSeverity(value);
  const display = (
    <PercentageFormatter value={value} isRatio={false} />
  );

  if (!severity) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(8),
        ...pacingSeverityStyles[severity](theme),
      })}
    >
      {display}
    </Box>
  );
};

const RemainingBudgetPercentagePill = ({ value }) => {
  const metric = parsePercentageValue(value);
  const display = (
    <PercentageFormatter value={value} isRatio={false} />
  );

  if (metric === null || metric >= 5) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(8),
        color: theme.palette.error.dark,
        backgroundColor: safeAlpha(
          theme.palette.error.light || theme.palette.error.main,
          0.35
        ),
        borderColor: safeAlpha(theme.palette.error.main, 0.45),
      })}
    >
      {display}
    </Box>
  );
};

const OutOfAreaRatePill = ({ value }) => {
  const metric = parseRatioValue(value);
  const limit = 0.2;
  const display = <PercentageFormatter value={value} />;

  if (metric === null || metric <= limit) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(6),
        color: theme.palette.error.dark,
        backgroundColor: safeAlpha(
          theme.palette.error.light || theme.palette.error.main,
          0.25
        ),
        borderColor: safeAlpha(theme.palette.error.main, 0.45),
      })}
    >
      {display}
    </Box>
  );
};

const AcceptedRatePill = ({ value }) => {
  const metric = parseRatioValue(value);
  const threshold = 0.8;
  const display = <PercentageFormatter value={value} />;

  if (metric === null || metric >= threshold) {
    return display;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(6),
        color: theme.palette.error.dark,
        backgroundColor: safeAlpha(
          theme.palette.error.light || theme.palette.error.main,
          0.25
        ),
        borderColor: safeAlpha(theme.palette.error.main, 0.45),
      })}
    >
      {display}
    </Box>
  );
};

const HighlightIfZero = ({ value, children }) => {
  const numeric = toFiniteNumber(value);
  const isZero = numeric === 0;

  if (!isZero) {
    return children;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(6),
        color: theme.palette.error.dark,
        backgroundColor: safeAlpha(
          theme.palette.error.light || theme.palette.error.main,
          0.25
        ),
        borderColor: safeAlpha(theme.palette.error.main, 0.45),
      })}
    >
      {children}
    </Box>
  );
};

const WholeDollarFormatter = (props) => (
  <DollarFormatter {...props} fractionDigits={0} />
);

const getExhaustionDate = (row) => {
  const remaining = Number(row?.budget_remaining);
  const yesterdaySpend = Number(row?.yesterday_spend);
  if (!Number.isFinite(remaining) || !Number.isFinite(yesterdaySpend) || yesterdaySpend <= 0 || remaining <= 0) {
    return null;
  }
  const daysUntilExhaustion = remaining / yesterdaySpend;
  const exhaustionDate = new Date();
  exhaustionDate.setDate(exhaustionDate.getDate() + Math.floor(daysUntilExhaustion));
  return exhaustionDate;
};

const formatExhaustionDate = (date) => {
  if (!date) return '-';
  return dayjs(date).format('MMM D');
};

const isExhaustionBeforeMonthEnd = (row) => {
  const date = getExhaustionDate(row);
  if (!date) return false;
  const now = new Date();
  const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return date <= lastDayOfMonth;
};

const ExhaustionDatePill = ({ row }) => {
  const date = getExhaustionDate(row);
  const label = formatExhaustionDate(date);
  const atRisk = isExhaustionBeforeMonthEnd(row);

  if (!date) {
    return <Typography variant="body2" color="text.secondary">-</Typography>;
  }

  return (
    <Box
      component="span"
      sx={(theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: '0.85rem',
        px: 1,
        py: 0.25,
        borderRadius: theme.shape.borderRadius * 2,
        border: '1px solid',
        minWidth: theme.spacing(8),
        ...(atRisk
          ? {
              color: theme.palette.error.dark,
              backgroundColor: safeAlpha(theme.palette.error.light || theme.palette.error.main, 0.35),
              borderColor: safeAlpha(theme.palette.error.main, 0.45),
            }
          : {
              color: theme.palette.success.dark,
              backgroundColor: safeAlpha(theme.palette.success.light || theme.palette.success.main, 0.2),
              borderColor: safeAlpha(theme.palette.success.main, 0.35),
            }),
      })}
    >
      {label}
    </Box>
  );
};

const getRemainingBudgetPercentage = (remaining, budget) => {
  const remainingValue = Number(remaining);
  const budgetValue = Number(budget);
  if (
    !Number.isFinite(remainingValue) ||
    !Number.isFinite(budgetValue) ||
    budgetValue === 0
  ) {
    return null;
  }

  const percent = (remainingValue / budgetValue) * 100;
  return Number.isFinite(percent) ? percent : null;
};

const getFacebookBudgetPacePercentage = (row) => {
  const dailyBudget = Number(row?.facebook_daily_budget_total ?? row?.facebook_budget_amount);
  const remainingBudgetByDay = Number(
    row?.calendar_budget_remaining_per_day ?? row?.remaining_per_day
  );
  if (
    !Number.isFinite(dailyBudget) ||
    dailyBudget <= 0 ||
    !Number.isFinite(remainingBudgetByDay) ||
    remainingBudgetByDay <= 0
  ) {
    return null;
  }

  return (dailyBudget / remainingBudgetByDay) * 100;
};

const deriveRates = (row) => {
  const acceptedRateFromSums = safeRatio(row?.accepted_sum, row?.accepted_sum, row?.rejected_sum);
  const outOfAreaRateFromSums = safeRatio(row?.out_of_areas_count_sum, row?.total_leads_sum);
  const selfBookRateFromSums = safeRatio(row?.self_books_sum, row?.total_leads_sum);

  return {
    acceptedRate: acceptedRateFromSums ?? row?.accepted_avg,
    outOfAreaRate: outOfAreaRateFromSums ?? row?.out_of_area_avg,
    selfBookRate: selfBookRateFromSums ?? row?.self_book_rate_avg,
  };
};

const sumRowValues = (rows, selector) =>
  rows.reduce((total, row) => {
    const value = toFiniteNumber(selector(row));
    return total + (value ?? 0);
  }, 0);

const buildAggregateRow = (rows, overrides = {}) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  const totalSpend = sumRowValues(rows, (row) => row?.total_spend_sum);
  const totalSpendPlusFees = sumRowValues(rows, (row) => row?.spendPlusFees);
  const totalProjectedSpend = sumRowValues(rows, (row) => row?.projected_spend);
  const totalBudget = sumRowValues(rows, (row) => row?.budget_amount);
  const totalBudgetRemaining = sumRowValues(rows, (row) => row?.budget_remaining);
  const totalRemainingPerDay = sumRowValues(
    rows,
    (row) => row?.calendar_budget_remaining_per_day
  );
  const totalYesterdaySpend = sumRowValues(rows, (row) => row?.yesterday_spend);
  const totalFacebookBudget = sumRowValues(
    rows,
    (row) => row?.facebook_daily_budget_total ?? row?.facebook_budget_amount
  );
  const totalLeads = sumRowValues(rows, (row) => row?.total_leads_sum);
  const totalAccepted = sumRowValues(rows, (row) => row?.accepted_sum);
  const totalRejected = sumRowValues(rows, (row) => row?.rejected_sum);
  const totalOutOfArea = sumRowValues(rows, (row) => row?.out_of_areas_count_sum);
  const totalSelfBooks = sumRowValues(rows, (row) => row?.self_books_sum);
  const totalBooked = sumRowValues(rows, (row) => row?.booked_sum);
  const totalScheduled = sumRowValues(rows, (row) => row?.scheduled_sum);
  const totalIssues = sumRowValues(rows, (row) => row?.issues_sum);
  const totalDemos = sumRowValues(rows, (row) => row?.demos_sum);
  const totalGrossSales = sumRowValues(rows, (row) => row?.gross_sales_sum);
  const totalGrossAmount = sumRowValues(rows, (row) => row?.gross_amount_sum);
  const totalNetSales = sumRowValues(rows, (row) => row?.net_sales_sum);
  const totalNetAmount = sumRowValues(rows, (row) => row?.net_amount_sum);

  const costPerLead =
    totalLeads > 0 ? totalSpend / totalLeads : null;
  const costPerSelfBook =
    totalSelfBooks > 0 ? totalSpend / totalSelfBooks : null;
  const costOfMarketing =
    totalNetAmount > 0 ? totalSpendPlusFees / totalNetAmount : null;
  const pacingPercentage =
    totalBudget > 0 ? (totalProjectedSpend / totalBudget) * 100 : null;

  // A shared goal only makes sense when every child shares it.
  const goals = new Set(
    rows
      .map((row) => row?.costOfMarketingGoal)
      .filter((goal) => typeof goal === 'number' && Number.isFinite(goal))
  );
  const costOfMarketingGoal = goals.size === 1 ? [...goals][0] : null;
  const hasDraftBudget = rows.some(
    (row) => row?.budget_workflow_status === 'draft'
  );

  return {
    uuid: 'daily-pulse-total-row',
    client__name: 'Total',
    costOfMarketingGoal,
    ...(hasDraftBudget ? { budget_workflow_status: 'draft' } : {}),
    total_spend_sum: totalSpend,
    spendPlusFees: totalSpendPlusFees,
    costOfMarketing,
    projected_spend: totalProjectedSpend,
    pacing_percentage: pacingPercentage,
    budget_amount: totalBudget,
    budget_remaining: totalBudgetRemaining,
    calendar_budget_remaining_per_day: totalRemainingPerDay,
    yesterday_spend: totalYesterdaySpend,
    facebook_daily_budget_total: totalFacebookBudget,
    total_leads_sum: totalLeads,
    cost_per_lead_avg: costPerLead,
    accepted_sum: totalAccepted,
    rejected_sum: totalRejected,
    out_of_areas_count_sum: totalOutOfArea,
    self_books_sum: totalSelfBooks,
    cost_per_self_book_avg: costPerSelfBook,
    booked_sum: totalBooked,
    scheduled_sum: totalScheduled,
    issues_sum: totalIssues,
    demos_sum: totalDemos,
    gross_sales_sum: totalGrossSales,
    gross_amount_sum: totalGrossAmount,
    net_sales_sum: totalNetSales,
    net_amount_sum: totalNetAmount,
    ...overrides,
  };
};

const buildClientTotalsRow = (rows) => buildAggregateRow(rows);

const GOOGLE_ADS_STATIC_PARAMS =
  '&euid=391788443&__u=1069503107&uscid=7044144174&__c=3239473726&authuser=0&__e=2441890378';

const buildGoogleAdsLink = (customerId) =>
  `https://ads.google.com/aw/overview?ocid=${customerId}&ascid=${customerId}${GOOGLE_ADS_STATIC_PARAMS}`;

const toLookupKey = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  return String(value);
};

const buildClientAdAccountLookup = (clients) => {
  if (!Array.isArray(clients)) {
    return {};
  }

  return clients.reduce((acc, client = {}) => {
    const key = toLookupKey(client?.id);
    if (!key) {
      return acc;
    }

    const facebookLinks = (Array.isArray(client.facebook_accounts) ? client.facebook_accounts : [])
      .filter((account) => account?.account_id)
      .map((account) => ({
        key: `fb-${account.account_id}`,
        href: `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${account.account_id}`,
        label: `Facebook: ${account.account_name || account.account_id}`,
        title: account.account_name || account.account_id,
        platform: 'facebook',
      }));

    const tiktokLinks = (Array.isArray(client.tiktok_advertisers) ? client.tiktok_advertisers : [])
      .filter((advertiser) => advertiser?.advertiser_id)
      .map((advertiser) => ({
        key: `tt-${advertiser.advertiser_id}`,
        href: `https://ads.tiktok.com/i18n/manage/campaign?aadvid=${advertiser.advertiser_id}`,
        label: `TikTok: ${advertiser.advertiser_name || advertiser.advertiser_id}`,
        title: advertiser.advertiser_name || advertiser.advertiser_id,
        platform: 'tiktok',
      }));

    const googleLinks = (Array.isArray(client.google_customers) ? client.google_customers : [])
      .filter((customer) => customer?.customer_id)
      .map((customer) => ({
        key: `gg-${customer.customer_id}`,
        href: buildGoogleAdsLink(customer.customer_id),
        label: `Google: ${customer.customer_name || customer.customer_id}`,
        title: customer.customer_name || customer.customer_id,
        platform: 'google',
      }));

    const links = [...facebookLinks, ...tiktokLinks, ...googleLinks];
    if (links.length > 0) {
      acc[key] = links;
    }
    return acc;
  }, {});
};

const BUDGET_OPTIMIZER_PATH = '/facebook/budget-optimizer';

// Maps a client's optimizer config to a compact status chip. `null` config = no
// config row at all ("set up"). Disabled config = exists but off.
const optimizerStatusMeta = (config) => {
  if (!config) {
    return { label: 'Optimizer: set up', color: 'default', variant: 'outlined', needsConfig: true };
  }
  if (!config.enabled) {
    return { label: 'Optimizer: off', color: 'default', variant: 'outlined', needsConfig: true };
  }
  switch (config.mode) {
    case 'auto_apply':
      return { label: 'Auto', color: 'success', variant: 'filled', needsConfig: false };
    case 'manual_approve':
      return { label: 'Manual approve', color: 'warning', variant: 'outlined', needsConfig: false };
    default:
      return { label: 'Dry run', color: 'info', variant: 'outlined', needsConfig: false };
  }
};

const FacebookBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" role="img" aria-label="Facebook" {...props}>
    <rect width="16" height="16" rx="4" fill="#1877F2" />
    <path
      d="M9.333 6H10.667V4.133H9.4C7.933 4.133 6.933 5.133 6.933 6.6V7.8H5.333V9.933H6.933V16H9.4V9.933H11L11.333 7.8H9.4V6.733C9.4 6.293 9.667 6 9.333 6Z"
      fill="#fff"
    />
  </svg>
);

const GoogleBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 18 18" role="img" aria-label="Google" {...props}>
    <rect x="1" y="1" width="16" height="16" rx="8" fill="#fff" stroke="#E0E0E0" strokeWidth="0.5" />
    <path
      fill="#4285F4"
      d="M16.318 8.205c0-.546-.049-1.07-.141-1.574H9v2.977h4.082c-.175.95-.702 1.758-1.494 2.295v1.908h2.414c1.414-1.302 2.316-3.219 2.316-5.606z"
    />
    <path
      fill="#34A853"
      d="M9 16.5c1.98 0 3.64-.655 4.853-1.776l-2.414-1.908c-.67.45-1.53.717-2.439.717-1.874 0-3.463-1.266-4.032-2.966H2.448v1.996A7.495 7.495 0 009 16.5z"
    />
    <path
      fill="#FBBC05"
      d="M4.968 10.567A4.495 4.495 0 014.73 9c0-.548.094-1.084.238-1.567V5.437H2.448A7.5 7.5 0 001.5 9c0 1.21.289 2.352.948 3.563l2.52-1.996z"
    />
    <path
      fill="#EA4335"
      d="M9 4.5c1.08 0 2.045.372 2.804 1.098l2.099-2.099C12.64 2.387 10.98 1.5 9 1.5a7.495 7.495 0 00-6.552 3.937l2.52 1.996C5.537 5.766 7.126 4.5 9 4.5z"
    />
  </svg>
);

const TikTokBadgeIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" role="img" aria-label="TikTok" {...props}>
    <rect width="16" height="16" rx="4" fill="#010101" />
    <path
      d="M7.2 13.6c-1.43 0-2.6-1.17-2.6-2.6 0-1.177.784-2.167 1.86-2.483v1.667a.95.95 0 00-.56.876c0 .526.43.956.96.956a1.01 1.01 0 00.7-.296V4.8h2.3c.026 1.232 1.018 2.224 2.25 2.25V8.8a3.47 3.47 0 01-2.25-.741v3.115c0 1.745-1.415 3.159-3.16 3.159Z"
      fill="#25F4EE"
    />
    <path
      d="M6.4 12.667c-1.01 0-1.833-.822-1.833-1.833 0-.42.146-.806.39-1.11V6.133H3.6v2.266c-.84.504-1.4 1.414-1.4 2.435 0 1.58 1.28 2.86 2.86 2.86 1.014 0 1.9-.517 2.433-1.302v-1.767c-.223.17-.5.272-.813.272-.69 0-1.25-.56-1.25-1.25 0-.07.006-.14.017-.207"
      fill="#FE2C55"
    />
    <path
      d="M10.6 5.2V4H8.3v7.633a1.02 1.02 0 01-.76.323c-.54 0-.98-.44-.98-.98 0-.54.44-.98.98-.98.34 0 .64.18.807.443V7.214c.62.43 1.377.686 2.173.686.2 0 .39-.017.58-.045V6.8a3.51 3.51 0 01-1.5-.934Z"
      fill="#fff"
    />
  </svg>
);

const PLATFORM_ICON_COMPONENTS = {
  facebook: FacebookBadgeIcon,
  google: GoogleBadgeIcon,
  tiktok: TikTokBadgeIcon,
};

const COLUMNS = [
  { id: 'client', label: 'Client', sortField: 'client__name', locked: true },
  { id: 'ad_accounts', label: 'Ad Accounts' },
  { id: 'cost_of_marketing', label: 'Cost of Mktg', sortField: 'costOfMarketing' },
  { id: 'total_spend', label: 'Spend', sortField: 'total_spend_sum' },
  { id: 'spend_plus_fees', label: 'Spend + Fees', sortField: 'spendPlusFees' },
  { id: 'projected_spend', label: 'Proj. Spend', sortField: 'projected_spend' },
  { id: 'pacing', label: 'Pacing %', sortField: 'pacing_percentage' },
  { id: 'budget', label: 'Budget', sortField: 'budget_amount' },
  { id: 'budget_remaining', label: 'Remaining', sortField: 'budget_remaining' },
  { id: 'remaining_per_day', label: 'Rem. /Day', sortField: 'calendar_budget_remaining_per_day' },
  { id: 'yesterday_spend', label: 'Yest. Spend', sortField: 'yesterday_spend' },
  { id: 'exhaustion_date', label: 'Budget Runs Out' },
  { id: 'fb_budget', label: 'FB Budget', sortField: 'facebook_daily_budget_total' },
  { id: 'fb_pace', label: 'FB Pace %' },
  { id: 'leads', label: 'Leads', sortField: 'total_leads_sum' },
  { id: 'cpl', label: 'CP Lead', sortField: 'cost_per_lead_avg' },
  { id: 'accepted_rate', label: 'Accept Rate', sortField: 'accepted_avg' },
  { id: 'out_rate', label: 'Out Rate', sortField: 'out_of_area_avg' },
  { id: 'self_books', label: 'Self Books', sortField: 'self_books_sum' },
  { id: 'cp_self_book', label: 'CP Self Book', sortField: 'cost_per_self_book_avg' },
  { id: 'self_book_rate', label: 'SB Rate', sortField: 'self_book_rate_avg' },
  { id: 'booked', label: 'Booked', sortField: 'booked_sum' },
  { id: 'scheduled', label: 'Scheduled', sortField: 'scheduled_sum' },
  { id: 'issues', label: 'Issues', sortField: 'issues_sum' },
  { id: 'demos', label: 'Demos', sortField: 'demos_sum' },
  { id: 'gross_sales', label: 'Gross Sales', sortField: 'gross_sales_sum' },
  { id: 'gross_amount', label: 'Gross Amt', sortField: 'gross_amount_sum' },
  { id: 'net_sales', label: 'Net Sales', sortField: 'net_sales_sum' },
  { id: 'net_amount', label: 'Net Amt', sortField: 'net_amount_sum' },
];

const DEFAULT_VISIBLE_COLS = new Set(COLUMNS.filter((c) => c.defaultVisible !== false).map((c) => c.id));
const COLUMN_VISIBILITY_STORAGE_KEY = 'daily_pulse_visible_columns';

const getInitialVisibleColumns = () => {
  if (typeof window === 'undefined') return DEFAULT_VISIBLE_COLS;
  try {
    const raw = window.localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
    if (!raw) return DEFAULT_VISIBLE_COLS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_VISIBLE_COLS;
    const validIds = new Set(COLUMNS.map((c) => c.id));
    const restored = parsed.filter((id) => validIds.has(id));
    // Always include locked columns
    COLUMNS.forEach((c) => { if (c.locked && !restored.includes(c.id)) restored.push(c.id); });
    return restored.length > 0 ? new Set(restored) : DEFAULT_VISIBLE_COLS;
  } catch {
    return DEFAULT_VISIBLE_COLS;
  }
};

const persistVisibleColumns = (colSet) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify([...colSet]));
  } catch { /* ignore */ }
};

const DATE_PRESETS = [
  { label: 'This Month', getRange: () => { const now = new Date(); return [new Date(now.getFullYear(), now.getMonth(), 1), new Date(now.getFullYear(), now.getMonth() + 1, 0)]; } },
  { label: 'Last 7d', getRange: () => { const end = new Date(); end.setDate(end.getDate() - 1); const start = new Date(end); start.setDate(start.getDate() - 6); return [start, end]; } },
  { label: 'Last 30d', getRange: () => { const end = new Date(); end.setDate(end.getDate() - 1); const start = new Date(end); start.setDate(start.getDate() - 29); return [start, end]; } },
  { label: 'Last Month', getRange: () => { const now = new Date(); return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 0)]; } },
];

const SKELETON_ROWS = 8;

export default function DailyPulse() {
  const dailyPulse = useDailyPulse() ?? {};
  const {
    data,
    clientSummary,
    startDate,
    endDate,
    setDateRange: setDateRangeFromContext,
    isLoading = false,
    refreshData: refreshDataFromContext,
    lastRefreshedAt = null,
    clientStatusFilter = DEFAULT_CLIENT_STATUS_FILTER,
    setClientStatusFilter: setClientStatusFilterFromContext,
    clientTierFilter = [],
    setClientTierFilter: setClientTierFilterFromContext,
  } = dailyPulse;

  const rows = React.useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const [clientAccountLookup, setClientAccountLookup] = React.useState({});
  const [clientSlugLookup, setClientSlugLookup] = React.useState({});
  const [parentCompanyLookup, setParentCompanyLookup] = React.useState({});
  // client_id -> { enabled, mode } so each row can show its optimizer status.
  const [optimizerByClient, setOptimizerByClient] = React.useState({});

  // Sorting is fully client-side now that all rows load in one fetch.
  const [orderBy, setOrderBy] = React.useState('total_spend_sum');
  const [orderDirection, setOrderDirection] = React.useState('desc');

  // Column picker state
  const [visibleCols, setVisibleCols] = React.useState(getInitialVisibleColumns);
  const [colPickerAnchor, setColPickerAnchor] = React.useState(null);
  const isCol = React.useCallback((id) => visibleCols.has(id), [visibleCols]);
  const toggleColumn = React.useCallback((id) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      persistVisibleColumns(next);
      return next;
    });
  }, []);

  // Search state
  const [searchQuery, setSearchQuery] = React.useState('');
  const searchRef = React.useRef(null);

  // Parent-company group expansion + KPI quick filter
  const [expandedParents, setExpandedParents] = React.useState(new Set());
  const [quickFilter, setQuickFilter] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;

    // Ad accounts / slugs / parent names don't vary by date range, so fetch once.
    const params = {
      fields:
        'id,slug,parent_company,parent_company_name,facebook_accounts,tiktok_advertisers,google_customers',
      page_size: 500,
    };

    api
      .request({
        url: 'dashboard/clients/',
        method: 'GET',
        params,
      })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        const payload = response?.data;
        const clients = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload)
            ? payload
            : [];
        setClientAccountLookup(buildClientAdAccountLookup(clients));
        const slugMap = clients.reduce((acc, client) => {
          const key = toLookupKey(client?.id);
          if (key && client?.slug) {
            acc[key] = client.slug;
          }
          return acc;
        }, {});
        setClientSlugLookup(slugMap);
        const parentMap = clients.reduce((acc, client) => {
          const key = toLookupKey(client?.id);
          if (!key) {
            return acc;
          }
          acc[key] = client?.parent_company_name || null;
          return acc;
        }, {});
        setParentCompanyLookup(parentMap);
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Failed to fetch client ad accounts for Daily Pulse', error);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Budget-optimizer config per client (mode + enabled) for the status chip in the
  // Ad Accounts column. Doesn't vary by date range, so fetch once.
  React.useEffect(() => {
    let isMounted = true;
    api
      .request({
        url: 'dashboard/facebook/budget-optimizer/configs/',
        method: 'GET',
        params: { page_size: 1000 },
      })
      .then((response) => {
        if (!isMounted) return;
        const payload = response?.data;
        const configs = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload)
            ? payload
            : [];
        const map = configs.reduce((acc, cfg) => {
          const key = toLookupKey(cfg?.client);
          if (key) acc[key] = { enabled: cfg.enabled, mode: cfg.mode };
          return acc;
        }, {});
        setOptimizerByClient(map);
      })
      .catch((error) => {
        if (isMounted) {
          console.error('Failed to fetch budget optimizer configs for Daily Pulse', error);
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const resolvedStatusFilter = Array.isArray(clientStatusFilter)
    ? clientStatusFilter
    : DEFAULT_CLIENT_STATUS_FILTER;
  const resolvedTierFilter = Array.isArray(clientTierFilter) ? clientTierFilter : [];

  const updateStatusFilter = React.useCallback(
    (next) => {
      if (typeof setClientStatusFilterFromContext === 'function') {
        setClientStatusFilterFromContext(next);
      }
    },
    [setClientStatusFilterFromContext],
  );

  const updateTierFilter = React.useCallback(
    (next) => {
      if (typeof setClientTierFilterFromContext === 'function') {
        setClientTierFilterFromContext(next);
      }
    },
    [setClientTierFilterFromContext],
  );

  const normalizeMultiValue = (value) => {
    if (typeof value === 'string') {
      return value.split(',').filter(Boolean);
    }
    return Array.isArray(value) ? value : [];
  };

  const handleStatusFilterChange = (event) => {
    updateStatusFilter(normalizeMultiValue(event.target.value));
  };

  const handleTierFilterChange = (event) => {
    updateTierFilter(normalizeMultiValue(event.target.value));
  };

  const tierOptions = React.useMemo(() => {
    const byId = new Map();
    rows.forEach((row = {}) => {
      const tierId = row?.client__tier;
      if (tierId === null || tierId === undefined) {
        return;
      }
      const key = String(tierId);
      if (!byId.has(key)) {
        byId.set(key, {
          value: key,
          label: row?.['client__tier__name'] || `Tier ${key}`,
        });
      }
    });
    return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const tierLabelLookup = React.useMemo(
    () =>
      tierOptions.reduce((acc, option) => {
        acc[option.value] = option.label;
        return acc;
      }, {}),
    [tierOptions],
  );

  const getSortValue = React.useCallback((row, field) => {
    if (!row) {
      return null;
    }
    if (field === 'client__name') {
      return (row.client__name || '').toLowerCase();
    }
    if (field === 'accepted_avg') {
      return parseRatioValue(deriveRates(row).acceptedRate);
    }
    if (field === 'out_of_area_avg') {
      return parseRatioValue(deriveRates(row).outOfAreaRate);
    }
    if (field === 'self_book_rate_avg') {
      return parseRatioValue(deriveRates(row).selfBookRate);
    }
    return toFiniteNumber(row[field]);
  }, []);


  const renderStatusValue = React.useCallback((selected) => {
    if (
      !Array.isArray(selected) ||
      selected.length === 0 ||
      selected.length === CLIENT_STATUS_OPTIONS.length
    ) {
      return 'All Statuses';
    }
    return selected.map((value) => STATUS_LABEL_LOOKUP[value] || value).join(', ');
  }, []);

  const renderTierValue = React.useCallback(
    (selected) => {
      if (
        !Array.isArray(selected) ||
        selected.length === 0 ||
        (tierOptions.length > 0 && selected.length === tierOptions.length)
      ) {
        return 'All Tiers';
      }
      return selected.map((value) => tierLabelLookup[value] || value).join(', ');
    },
    [tierLabelLookup, tierOptions.length],
  );

  const setDateRange = React.useCallback((...args) => {
    if (typeof setDateRangeFromContext === 'function') {
      setDateRangeFromContext(...args);
    }
  }, [setDateRangeFromContext]);

  const setSort = React.useCallback((field) => {
    setOrderBy((prevField) => {
      if (prevField === field) {
        setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        return prevField;
      }
      setOrderDirection(field === 'client__name' ? 'asc' : 'desc');
      return field;
    });
  }, []);

  // Expand/collapse parent-company groups
  const toggleParentExpanded = React.useCallback((groupKey) => {
    if (!groupKey) return;
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) { next.delete(groupKey); } else { next.add(groupKey); }
      return next;
    });
  }, []);

  // Date presets
  const handleDatePreset = React.useCallback((preset) => {
    const [start, end] = preset.getRange();
    if (typeof setDateRangeFromContext === 'function') {
      setDateRangeFromContext(start, end);
    }
  }, [setDateRangeFromContext]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handler = (e) => {
      // Don't capture when typing in an input
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        if (typeof refreshDataFromContext === 'function') refreshDataFromContext();
      }
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [refreshDataFromContext]);

  const handleRefresh = React.useCallback(() => {
    if (typeof refreshDataFromContext === 'function') {
      refreshDataFromContext();
    }
  }, [refreshDataFromContext]);

  const lastRefreshedLabel = React.useMemo(() => {
    if (!lastRefreshedAt) {
      return 'Not refreshed yet';
    }
    const refreshed = new Date(lastRefreshedAt);
    if (Number.isNaN(refreshed.getTime())) {
      return 'Not refreshed yet';
    }
    return refreshed.toLocaleString();
  }, [lastRefreshedAt]);

  const handleSort = (field) => setSort(field);

  const handleDateRangeChange = (e) => {
    const { startDate, endDate } = e.selection;
    setDateRange(startDate, endDate);
  };

  const stickyHeaderBaseSx = {
    position: 'sticky',
    top: 0,
    backgroundColor: (theme) => theme.palette.background.paper,
    zIndex: 3
  };

  const stickyClientHeaderSx = {
    ...stickyHeaderBaseSx,
    left: 0,
    minWidth: 200,
    whiteSpace: 'normal',
    lineHeight: 1.2
  };

  const stickyClientCellSx = {
    position: 'sticky',
    left: 0,
    backgroundColor: (theme) => theme.palette.background.paper,
    zIndex: 2,
    minWidth: 200,
    maxWidth: 300,
    whiteSpace: 'normal',
    lineHeight: 1.2,
  };

  const tableContainerSx = {
    maxHeight: '75vh',
    minHeight: 520,
    borderRadius: 2,
    border: (theme) => `1px solid ${theme.palette.divider}`,
    boxShadow: (theme) => theme.shadows[1],
    backgroundColor: (theme) => theme.palette.background.paper,
  };

  const mainTableSx = {
    minWidth: 1600,
    '& .MuiTableCell-root': {
      borderBottomColor: (theme) => safeAlpha(theme?.palette?.divider, 0.7),
      paddingTop: (theme) => theme.spacing(1),
      paddingBottom: (theme) => theme.spacing(1),
    },
    '& thead .MuiTableCell-root': {
      whiteSpace: 'normal',
      lineHeight: 1.2,
    },
    '& tbody .MuiTableCell-root': {
      whiteSpace: 'nowrap',
    },
    '& tbody tr': {
      backgroundColor: (theme) => theme.palette.background.paper,
    },
    '& tbody tr:hover': {
      backgroundColor: (theme) => getHoverBackground(theme, 0.06),
    },
  };

  const clientNameLinkSx = {
    textDecoration: 'none',
    fontWeight: 600,
    display: 'block',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.2,
    '&:hover': {
      textDecoration: 'underline',
    },
  };

  const clientAccountLinkSx = {
    color: 'text.secondary',
    textDecoration: 'none',
    fontSize: '0.7rem',
    lineHeight: 1.1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: (theme) => theme.palette.background.default,
    transition: 'all 0.15s ease',
    '&:hover': {
      textDecoration: 'none',
      transform: 'translateY(-1px)',
      boxShadow: (theme) => theme.shadows[1],
    },
  };

  const clientAccountCellSx = {
    minWidth: 120,
    maxWidth: 160,
    whiteSpace: 'normal',
    lineHeight: 1.2,
    wordBreak: 'break-word',
  };

  const getClientNameColor = (status, theme) => {
    if (PRIORITY_CLIENT_STATUS_SET.has(status)) {
      return theme.palette.mode === 'dark' ? PURPLE_STATUS_COLOR_DARK : PURPLE_STATUS_COLOR;
    }
    return theme.palette.primary?.main || PURPLE_STATUS_COLOR;
  };

  const getClientNameSx = (status) => (theme) => ({
    ...clientNameLinkSx,
    color: getClientNameColor(status, theme),
  });

  const LaunchDateBadge = ({ status, label }) => {
    if (!label) {
      return null;
    }
    return (
      <Typography
        component="span"
        sx={(theme) => {
          const color = getClientNameColor(status, theme);
          return {
            fontSize: '0.72rem',
            fontWeight: 600,
            color,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          };
        }}
      >
        {label}
      </Typography>
    );
  };

  const buildClientDashboardPath = (clientId) => {
    if (!clientId) {
      return null;
    }
    const key = toLookupKey(clientId);
    const slug = key ? clientSlugLookup[key] : null;
    const identifier = slug || clientId;
    return `/clients/${identifier}/dashboard`;
  };

  const normalizeSlug = (value = '') =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const getParentNameFromRow = React.useCallback(
    (row = {}) =>
      row.client__parent_company__name ||
      parentCompanyLookup[toLookupKey(row.client_id)] ||
      row.parent_company_name ||
      null,
    [parentCompanyLookup]
  );

  // All displayable client rows before search/quick filters. KPI counts and
  // the totals row stay stable no matter what is being searched.
  //
  // Default "All Statuses" view applies the command-center rule: active /
  // active-new clients always show; every other status (onboarding, paused,
  // off, new business) only shows when it actually spent in the range — an
  // onboarding client with a submitted budget but no live ads isn't pulse
  // material yet. Explicitly selecting statuses in the dropdown bypasses
  // this and shows exactly what was asked for.
  const isDefaultStatusView = resolvedStatusFilter.length === 0;
  const baseClientRows = React.useMemo(
    () =>
      rows.filter((row) => {
        if (!row?.client__name || !shouldDisplayClientRow(row)) {
          return false;
        }
        if (!isDefaultStatusView) {
          return true;
        }
        const clientStatus = row.client__status;
        if (clientStatus === 'active' || clientStatus === 'active_new') {
          return true;
        }
        return Boolean(toPositiveNumber(row.total_spend_sum ?? row.total_spend));
      }),
    [rows, isDefaultStatusView]
  );

  const matchesQuickFilter = React.useCallback(
    (row) => {
      if (!quickFilter) return true;
      // Pacing alerts skip intentionally held/paused clients (matching the
      // KPI counts) — those live under the "Paused / held" chip instead.
      if (quickFilter === 'over') {
        const pacing = parsePercentageValue(row?.pacing_percentage);
        return !isHeldOrOff(row) && pacing !== null && pacing > 110;
      }
      if (quickFilter === 'under') {
        const pacing = parsePercentageValue(row?.pacing_percentage);
        return !isHeldOrOff(row) && pacing !== null && pacing < 70;
      }
      if (quickFilter === 'exhaust') {
        return !isHeldOrOff(row) && isExhaustionBeforeMonthEnd(row);
      }
      if (quickFilter === 'data') {
        return getIngestionStatusKey(row?.ingestion_health) === 'warning';
      }
      if (quickFilter === 'held') {
        return isHeldOrOff(row);
      }
      if (quickFilter === 'pastdue') {
        return isPastDuePaused(row);
      }
      if (quickFilter === 'new') {
        return PRIORITY_CLIENT_STATUS_SET.has(row?.client__status);
      }
      return true;
    },
    [quickFilter]
  );

  const matchesSearch = React.useCallback(
    (row) => {
      const q = searchQuery.trim().toLowerCase();
      if (!q) return true;
      const name = (row.client__name || '').toLowerCase();
      const parent = (getParentNameFromRow(row) || '').toLowerCase();
      return name.includes(q) || parent.includes(q);
    },
    [searchQuery, getParentNameFromRow]
  );

  const hasActiveRowFilter = Boolean(searchQuery.trim()) || Boolean(quickFilter);

  // Group clients under their parent company. Metrics roll up to the parent
  // row; children sit behind an expandable row. Rollups always cover ALL of a
  // parent's clients so numbers don't shift while searching/filtering — a
  // group is shown when any child matches, and groups auto-expand while a
  // search or quick filter is active.
  const groupedEntries = React.useMemo(() => {
    const entries = [];
    const groupsByKey = new Map();

    baseClientRows.forEach((row) => {
      const parentName = getParentNameFromRow(row);
      if (!parentName) {
        entries.push({
          type: 'client',
          key: `client-${row.uuid || row.client_id}`,
          row,
          children: [row],
        });
        return;
      }
      const groupKey = `parent-${row.client__parent_company ?? parentName}`;
      let group = groupsByKey.get(groupKey);
      if (!group) {
        group = { type: 'parent', key: groupKey, parentName, children: [] };
        groupsByKey.set(groupKey, group);
        entries.push(group);
      }
      group.children.push(row);
    });

    groupsByKey.forEach((group) => {
      group.row = buildAggregateRow(group.children, {
        uuid: group.key,
        client__name: group.parentName,
      });
    });

    const visibleEntries = entries.filter((entry) =>
      entry.children.some(
        (child) => matchesSearch(child) && matchesQuickFilter(child)
      )
    );

    const direction = orderDirection === 'asc' ? 1 : -1;
    const compareRows = (a, b) => {
      const aValue = getSortValue(a, orderBy);
      const bValue = getSortValue(b, orderBy);
      const aEmpty = aValue === null || aValue === undefined || aValue === '';
      const bEmpty = bValue === null || bValue === undefined || bValue === '';
      if (aEmpty || bEmpty) {
        if (aEmpty && bEmpty) return 0;
        return aEmpty ? 1 : -1;
      }
      if (typeof aValue === 'string' || typeof bValue === 'string') {
        return String(aValue).localeCompare(String(bValue)) * direction;
      }
      return (aValue - bValue) * direction;
    };

    // Pure metric sort — biggest spenders lead the view. New clients stay
    // findable via the purple styling + launch-date badge and the "New
    // clients" quick-filter chip instead of being pinned above whales.
    const sorted = [...visibleEntries].sort((a, b) => {
      const delta = compareRows(a.row, b.row);
      if (delta !== 0) return delta;
      return (a.row?.client__name || '').localeCompare(b.row?.client__name || '');
    });

    return sorted.map((entry) =>
      entry.type === 'parent'
        ? { ...entry, children: [...entry.children].sort(compareRows) }
        : entry
    );
  }, [
    baseClientRows,
    getParentNameFromRow,
    matchesSearch,
    matchesQuickFilter,
    orderBy,
    orderDirection,
    getSortValue,
  ]);

  const visibleClientCount = React.useMemo(
    () => groupedEntries.reduce((total, entry) => total + entry.children.length, 0),
    [groupedEntries]
  );

  const kpiStats = React.useMemo(() => {
    let over = 0;
    let under = 0;
    let exhaust = 0;
    let dataIssues = 0;
    let held = 0;
    let pastDue = 0;
    let newClients = 0;
    baseClientRows.forEach((row) => {
      if (PRIORITY_CLIENT_STATUS_SET.has(row?.client__status)) newClients += 1;
      if (isPastDuePaused(row)) pastDue += 1;
      const heldOrOff = isHeldOrOff(row);
      if (heldOrOff) held += 1;
      const pacing = parsePercentageValue(row?.pacing_percentage);
      // Intentionally held/paused clients shouldn't inflate the alert counts.
      if (!heldOrOff) {
        if (pacing !== null && pacing > 110) over += 1;
        if (pacing !== null && pacing < 70) under += 1;
        if (isExhaustionBeforeMonthEnd(row)) exhaust += 1;
      }
      if (getIngestionStatusKey(row?.ingestion_health) === 'warning') dataIssues += 1;
    });
    return {
      over,
      under,
      exhaust,
      dataIssues,
      held,
      pastDue,
      newClients,
      clients: baseClientRows.length,
    };
  }, [baseClientRows]);

  const totalsRow = React.useMemo(
    () => clientSummary || buildClientTotalsRow(baseClientRows),
    [clientSummary, baseClientRows]
  );

  const renderAccountLinkIcons = (links) => {
    if (!Array.isArray(links) || links.length === 0) {
      return null;
    }

    return (
      <Stack
        direction="row"
        spacing={0.25}
        flexWrap="nowrap"
        sx={{
          overflow: 'hidden',
          '& > *': { flexShrink: 0 },
        }}
      >
        {links.map((link) => {
          const IconComponent = PLATFORM_ICON_COMPONENTS[link.platform] || LinkSimple;
          return (
            <Tooltip key={link.key} title={link.label || link.title || ''} arrow>
              <Typography
                component="a"
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={clientAccountLinkSx}
              >
                <IconComponent size={14} />
              </Typography>
            </Tooltip>
          );
        })}
      </Stack>
    );
  };

  const renderClientAccountLinks = (clientId) => {
    const lookupKey = toLookupKey(clientId);
    if (!lookupKey) {
      return null;
    }
    return renderAccountLinkIcons(clientAccountLookup[lookupKey]);
  };

  // Compact budget-optimizer status chip → deep-links into the optimizer for this
  // client (Runs tab when active, Settings tab when there's nothing set up yet).
  const renderOptimizerStatus = (clientId) => {
    const key = toLookupKey(clientId);
    if (!key) {
      return null;
    }
    const meta = optimizerStatusMeta(optimizerByClient[key] || null);
    const href = `${BUDGET_OPTIMIZER_PATH}?client_id=${clientId}${meta.needsConfig ? '&tab=settings' : ''}`;
    return (
      <Tooltip
        title={meta.needsConfig ? 'No active budget optimizer — click to configure' : `Budget optimizer: ${meta.label}`}
        arrow
      >
        <Chip
          component={RouterLink}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          clickable
          size="small"
          color={meta.color}
          variant={meta.variant}
          label={meta.label}
          onClick={(e) => e.stopPropagation()}
          sx={{ height: 18, fontSize: '0.62rem', fontWeight: 600, cursor: 'pointer', '& .MuiChip-label': { px: 0.6 } }}
        />
      </Tooltip>
    );
  };

  // Parent rows show the deduped union of their children's ad accounts —
  // most parent companies share a single account, so this usually collapses
  // to one icon.
  const renderParentAccountLinks = (childrenRows) => {
    const links = [];
    const seen = new Set();
    (Array.isArray(childrenRows) ? childrenRows : []).forEach((child) => {
      const childLinks = clientAccountLookup[toLookupKey(child?.client_id)];
      (Array.isArray(childLinks) ? childLinks : []).forEach((link) => {
        if (!seen.has(link.key)) {
          seen.add(link.key);
          links.push(link);
        }
      });
    });
    return renderAccountLinkIcons(links);
  };

  const renderSortableHeader = (label, field, cellProps = {}) => (
    <TableCell sortDirection={orderBy === field ? orderDirection : false} {...cellProps}>
      <TableSortLabel
        active={orderBy === field}
        direction={orderBy === field ? orderDirection : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const compactHeaderSx = {
    maxWidth: 120,
    whiteSpace: 'normal',
    lineHeight: 1.2,
  };

  const extraCompactHeaderSx = {
    ...compactHeaderSx,
    maxWidth: 100,
  };

  const renderCostOfMarketingCell = (row, goalOverride = null) => {
    const goal = goalOverride ?? row.costOfMarketingGoal;
    const hasHistory =
      row.costOfMarketingLast30 !== undefined || row.costOfMarketingLast60 !== undefined;

    const tooltipContent = (
      <Stack spacing={0.6}>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Last 30 days
          </Typography>
          <Box mt={0.25}>
            <CostOfMarketingPill value={row.costOfMarketingLast30} goal={goal} />
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Last 60 days
          </Typography>
          <Box mt={0.25}>
            <CostOfMarketingPill value={row.costOfMarketingLast60} goal={goal} />
          </Box>
        </Box>
      </Stack>
    );

    const pill = <CostOfMarketingPill value={row.costOfMarketing} goal={goal} />;
    const pillWithTooltip = hasHistory ? (
      <Tooltip arrow placement="top" title={tooltipContent}>
        <Box component="span" sx={{ display: 'inline-flex' }}>
          {pill}
        </Box>
      </Tooltip>
    ) : (
      pill
    );

    return pillWithTooltip;
  };

  const renderDataCells = (row, opts = {}) => {
    const { acceptedRate, outOfAreaRate, selfBookRate } = deriveRates(row);
    const facebookBudgetPace = getFacebookBudgetPacePercentage(row);
    const remainingPct = getRemainingBudgetPercentage(row.budget_remaining, row.budget_amount);
    const isTotal = opts.isTotal;
    const indent = opts.indent || 0;
    const parentGroup = opts.parentGroup || null;

    return (
      <React.Fragment>
        {isCol('client') && (
          <TableCell sx={{ ...stickyClientCellSx, ...(indent ? { pl: 2 + indent * 2 } : {}) }}>
            {isTotal ? (
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.client__name || 'Total'}</Typography>
            ) : parentGroup ? (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                <IconButton
                  size="small"
                  sx={{ p: 0, flexShrink: 0 }}
                  onClick={(e) => { e.stopPropagation(); toggleParentExpanded(parentGroup.key); }}
                >
                  {parentGroup.expanded ? <KeyboardArrowDown sx={{ fontSize: 18 }} /> : <KeyboardArrowRight sx={{ fontSize: 18 }} />}
                </IconButton>
                <Typography
                  component={RouterLink}
                  href={parentGroup.dashboardPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="subtitle2"
                  onClick={(e) => e.stopPropagation()}
                  sx={(theme) => ({
                    ...clientNameLinkSx,
                    fontWeight: 700,
                    color: theme.palette.primary?.main || PURPLE_STATUS_COLOR,
                  })}
                >
                  {parentGroup.name}
                </Typography>
                <Typography component="span" variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  ({parentGroup.childCount})
                </Typography>
                <ParentIngestionIndicator childrenRows={parentGroup.childrenRows} />
                <ParentHoldIndicator childrenRows={parentGroup.childrenRows} />
              </Stack>
            ) : (
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                {row.client_id ? (
                  <Typography
                    component={RouterLink}
                    href={buildClientDashboardPath(row.client_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="subtitle2"
                    onClick={(e) => e.stopPropagation()}
                    sx={getClientNameSx(row.client__status)}
                  >
                    {row.client__name}
                  </Typography>
                ) : (
                  <Typography variant="subtitle2" sx={clientNameLinkSx}>
                    {row.client__name}
                  </Typography>
                )}
                {(() => {
                  const launchDateLabel = getClientLaunchDateLabel(row.client__status, row.client__launch_date);
                  return launchDateLabel ? <LaunchDateBadge status={row.client__status} label={launchDateLabel} /> : null;
                })()}
                <IngestionStatusIndicator ingestionHealth={row.ingestion_health} clientId={row.client_id} />
                <VolumeStatusIndicator ingestionHealth={row.ingestion_health} />
                {(() => {
                  // Non-payment pause is the hard reason; show it instead of
                  // the softer hold / "spend off" pills (which it would also
                  // trip, since paused campaigns spend $0).
                  const pastDue = getPastDuePause(row);
                  if (pastDue) {
                    return <PastDuePauseBadge pause={pastDue} />;
                  }
                  const holds = getActiveHolds(row);
                  if (holds.length > 0) {
                    return <HoldBadge holds={holds} />;
                  }
                  return isSpendOff(row) ? <SpendOffBadge /> : null;
                })()}
              </Stack>
            )}
          </TableCell>
        )}
        {isCol('ad_accounts') && (
          <TableCell sx={clientAccountCellSx}>
            {isTotal ? (
              '-'
            ) : parentGroup ? (
              renderParentAccountLinks(parentGroup.childrenRows)
            ) : (
              <Stack spacing={0.4} alignItems="flex-start">
                {renderClientAccountLinks(row.client_id)}
                {renderOptimizerStatus(row.client_id)}
              </Stack>
            )}
          </TableCell>
        )}
        {isCol('cost_of_marketing') && <TableCell>{renderCostOfMarketingCell(row)}</TableCell>}
        {isCol('total_spend') && <TableCell><WholeDollarFormatter value={row.total_spend_sum} /></TableCell>}
        {isCol('spend_plus_fees') && <TableCell><WholeDollarFormatter value={row.spendPlusFees} /></TableCell>}
        {isCol('projected_spend') && <TableCell><WholeDollarFormatter value={row.projected_spend} /></TableCell>}
        {isCol('pacing') && <TableCell><PacingPill value={row.pacing_percentage} /></TableCell>}
        {isCol('budget') && (
          <TableCell>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ display: 'inline-flex' }}>
              <WholeDollarFormatter value={row.budget_amount} />
              {row.budget_workflow_status === 'draft' ? (
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    px: 0.5,
                    py: 0.1,
                    borderRadius: 1,
                    border: '1px solid',
                    color: 'info.dark',
                    bgcolor: 'rgba(59,130,246,0.12)',
                    borderColor: 'rgba(59,130,246,0.4)',
                    lineHeight: 1,
                  }}
                >
                  Draft
                </Box>
              ) : null}
            </Stack>
          </TableCell>
        )}
        {isCol('budget_remaining') && (
          <TableCell>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <HighlightIfZero value={row.budget_remaining}>
                <WholeDollarFormatter value={row.budget_remaining} />
              </HighlightIfZero>
              {remainingPct !== null && (
                <Typography component="span" variant="caption" color="text.secondary">
                  (<RemainingBudgetPercentagePill value={remainingPct} />)
                </Typography>
              )}
            </Stack>
          </TableCell>
        )}
        {isCol('remaining_per_day') && <TableCell><WholeDollarFormatter value={row.calendar_budget_remaining_per_day} /></TableCell>}
        {isCol('yesterday_spend') && (
          <TableCell>
            <HighlightIfZero value={row.yesterday_spend}>
              <WholeDollarFormatter value={row.yesterday_spend} />
            </HighlightIfZero>
          </TableCell>
        )}
        {isCol('exhaustion_date') && (
          <TableCell>{isTotal ? '-' : <ExhaustionDatePill row={row} />}</TableCell>
        )}
        {isCol('fb_budget') && <TableCell><WholeDollarFormatter value={row.facebook_daily_budget_total} /></TableCell>}
        {isCol('fb_pace') && <TableCell><PacingPill value={facebookBudgetPace} /></TableCell>}
        {isCol('leads') && <TableCell>{row.total_leads_sum}</TableCell>}
        {isCol('cpl') && <TableCell><WholeDollarFormatter value={row.cost_per_lead_avg} /></TableCell>}
        {isCol('accepted_rate') && <TableCell><AcceptedRatePill value={acceptedRate} /></TableCell>}
        {isCol('out_rate') && <TableCell><OutOfAreaRatePill value={outOfAreaRate} /></TableCell>}
        {isCol('self_books') && <TableCell>{row.self_books_sum}</TableCell>}
        {isCol('cp_self_book') && <TableCell><WholeDollarFormatter value={row.cost_per_self_book_avg} /></TableCell>}
        {isCol('self_book_rate') && <TableCell><PercentageFormatter value={selfBookRate} /></TableCell>}
        {isCol('booked') && <TableCell>{row.booked_sum}</TableCell>}
        {isCol('scheduled') && <TableCell>{row.scheduled_sum}</TableCell>}
        {isCol('issues') && <TableCell>{row.issues_sum}</TableCell>}
        {isCol('demos') && <TableCell>{row.demos_sum}</TableCell>}
        {isCol('gross_sales') && <TableCell>{row.gross_sales_sum}</TableCell>}
        {isCol('gross_amount') && <TableCell><WholeDollarFormatter value={row.gross_amount_sum} /></TableCell>}
        {isCol('net_sales') && <TableCell>{row.net_sales_sum}</TableCell>}
        {isCol('net_amount') && <TableCell><WholeDollarFormatter value={row.net_amount_sum} /></TableCell>}
      </React.Fragment>
    );
  };

  const visibleColCount = COLUMNS.filter((c) => isCol(c.id)).length;

  return (
    <Box p={3}>
      <Card>
        <CardHeader
          title="Client Pulse"
          action={
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1, mt: 1 }}>
              <kbd style={{ fontSize: '0.75rem', padding: '1px 4px', border: '1px solid', borderRadius: 3, borderColor: 'inherit' }}>R</kbd> refresh
              {' '}<kbd style={{ fontSize: '0.75rem', padding: '1px 4px', border: '1px solid', borderRadius: 3, borderColor: 'inherit' }}>/</kbd> search
            </Typography>
          }
        />
        <Divider />
        <CardContent>
          {/* Toolbar row 1: Date range + presets + refresh */}
          <Stack direction="row" spacing={2} mb={1.5} alignItems="center" flexWrap="wrap">
            <Box>
              <Typography variant="caption" fontWeight={500} mb={0.5} display="block">
                Date Range
              </Typography>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                style={{ minWidth: 220 }}
              />
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="flex-end" sx={{ pb: 0.25 }}>
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.label}
                  size="small"
                  variant="text"
                  sx={{ textTransform: 'none', minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                  onClick={() => handleDatePreset(preset)}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>
            <Stack spacing={0.25}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRefresh}
                disabled={isLoading}
                startIcon={
                  isLoading ? <CircularProgress size={14} /> : <Refresh fontSize="small" />
                }
              >
                Refresh
              </Button>
              <Typography variant="caption" color="text.secondary">
                {lastRefreshedLabel}
              </Typography>
            </Stack>
          </Stack>

          {/* Toolbar row 2: Search + filters + column picker + parent links */}
          <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
            <TextField
              inputRef={searchRef}
              size="small"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 200, maxWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Box sx={{ minWidth: 220 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="client-status-filter-label">Client Status</InputLabel>
                <Select
                  labelId="client-status-filter-label"
                  multiple
                  value={resolvedStatusFilter}
                  onChange={handleStatusFilterChange}
                  input={<OutlinedInput label="Client Status" />}
                  renderValue={renderStatusValue}
                  MenuProps={MULTI_SELECT_MENU_PROPS}
                >
                  {CLIENT_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Checkbox checked={resolvedStatusFilter.indexOf(option.value) > -1} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: 220 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="client-tier-filter-label">Client Tier</InputLabel>
                <Select
                  labelId="client-tier-filter-label"
                  multiple
                  value={resolvedTierFilter}
                  onChange={handleTierFilterChange}
                  input={<OutlinedInput label="Client Tier" />}
                  renderValue={renderTierValue}
                  MenuProps={MULTI_SELECT_MENU_PROPS}
                >
                  {tierOptions.length === 0 ? (
                    <MenuItem disabled value="">
                      <ListItemText primary="No tiers available" />
                    </MenuItem>
                  ) : (
                    tierOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={resolvedTierFilter.indexOf(option.value) > -1} />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>
            <Tooltip title="Choose columns">
              <IconButton size="small" onClick={(e) => setColPickerAnchor(e.currentTarget)}>
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Popover
              open={Boolean(colPickerAnchor)}
              anchorEl={colPickerAnchor}
              onClose={() => setColPickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              PaperProps={{ sx: { maxHeight: 400, width: 260, p: 1 } }}
            >
              <Typography variant="subtitle2" sx={{ px: 1, pb: 0.5 }}>Visible Columns</Typography>
              <Button size="small" sx={{ textTransform: 'none', mb: 0.5 }} onClick={() => { setVisibleCols(new Set(DEFAULT_VISIBLE_COLS)); persistVisibleColumns(DEFAULT_VISIBLE_COLS); }}>
                Reset to default
              </Button>
              {COLUMNS.map((col) => (
                <MenuItem key={col.id} dense disabled={col.locked} onClick={() => !col.locked && toggleColumn(col.id)}>
                  <Checkbox size="small" checked={isCol(col.id)} disabled={col.locked} sx={{ p: 0.25, mr: 1 }} />
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{col.label}</ListItemText>
                </MenuItem>
              ))}
            </Popover>
          </Stack>

          {/* Command-center KPI strip — click a status chip to filter the table */}
          {!isLoading && baseClientRows.length > 0 ? (
            <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap alignItems="stretch">
              {(() => {
                const kpiBoxSx = (active, clickable) => (theme) => ({
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: active ? theme.palette.primary.main : theme.palette.divider,
                  backgroundColor: active
                    ? safeAlpha(theme.palette.primary.main, 0.08)
                    : theme.palette.background.paper,
                  cursor: clickable ? 'pointer' : 'default',
                  minWidth: 110,
                  '&:hover': clickable
                    ? { borderColor: theme.palette.primary.main }
                    : {},
                });
                const toggleQuickFilter = (key) =>
                  setQuickFilter((prev) => (prev === key ? null : key));
                const statChip = ({ key, label, value, color, clickable }) => (
                  <Box
                    key={key}
                    sx={kpiBoxSx(quickFilter === key, clickable)}
                    onClick={clickable ? () => toggleQuickFilter(key) : undefined}
                  >
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.2 }}>
                      {label}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3, color }}>
                      {value}
                    </Typography>
                  </Box>
                );
                const fmtMoney = (value) => {
                  const num = Number(value);
                  return Number.isFinite(num)
                    ? `$${Math.round(num).toLocaleString()}`
                    : '-';
                };
                const pacing = parsePercentageValue(totalsRow?.pacing_percentage);
                return (
                  <React.Fragment>
                    {statChip({ key: 'spend', label: 'Spend', value: fmtMoney(totalsRow?.total_spend_sum), clickable: false })}
                    {statChip({ key: 'budget', label: 'Budget', value: fmtMoney(totalsRow?.budget_amount), clickable: false })}
                    {statChip({ key: 'projected', label: 'Projected', value: fmtMoney(totalsRow?.projected_spend), clickable: false })}
                    {statChip({
                      key: 'pacing',
                      label: 'Pacing',
                      value: pacing !== null ? `${pacing.toFixed(1)}%` : '-',
                      clickable: false,
                    })}
                    {statChip({
                      key: 'over',
                      label: 'Over pace (>110%)',
                      value: kpiStats.over,
                      color: kpiStats.over > 0 ? 'error.main' : 'success.main',
                      clickable: true,
                    })}
                    {statChip({
                      key: 'under',
                      label: 'Under pace (<70%)',
                      value: kpiStats.under,
                      color: kpiStats.under > 0 ? '#6d28d9' : 'success.main',
                      clickable: true,
                    })}
                    {statChip({
                      key: 'exhaust',
                      label: 'Runs out early',
                      value: kpiStats.exhaust,
                      color: kpiStats.exhaust > 0 ? 'error.main' : 'success.main',
                      clickable: true,
                    })}
                    {statChip({
                      key: 'data',
                      label: 'Data issues',
                      value: kpiStats.dataIssues,
                      color: kpiStats.dataIssues > 0 ? 'warning.main' : 'success.main',
                      clickable: true,
                    })}
                    {statChip({
                      key: 'held',
                      label: 'Paused / held',
                      value: kpiStats.held,
                      clickable: true,
                    })}
                    {statChip({
                      key: 'pastdue',
                      label: 'Unpaid · paused',
                      value: kpiStats.pastDue,
                      color: kpiStats.pastDue > 0 ? 'error.main' : undefined,
                      clickable: true,
                    })}
                    {statChip({
                      key: 'new',
                      label: 'New clients',
                      value: kpiStats.newClients,
                      color: kpiStats.newClients > 0 ? PURPLE_STATUS_COLOR : undefined,
                      clickable: true,
                    })}
                  </React.Fragment>
                );
              })()}
            </Stack>
          ) : null}

          <TableContainer component={Paper} sx={tableContainerSx}>
            <Table size="medium" stickyHeader sx={mainTableSx}>
              <TableHead>
                <TableRow>
                  {COLUMNS.filter((c) => isCol(c.id)).map((col) =>
                    col.sortField
                      ? renderSortableHeader(col.label, col.sortField, {
                          key: col.id,
                          sx: col.id === 'client' ? stickyClientHeaderSx : compactHeaderSx,
                        })
                      : (
                        <TableCell
                          key={col.id}
                          sx={col.id === 'ad_accounts' ? clientAccountCellSx : compactHeaderSx}
                        >
                          {col.label}
                        </TableCell>
                      )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Totals row — pinned at top */}
                {totalsRow && !isLoading ? (
                  <TableRow
                    key="totals"
                    sx={(theme) => {
                      const tint = safeAlpha(theme.palette.info.light, 0.12);
                      return {
                        // Opaque cells + zIndex above the data rows' sticky
                        // client cells (zIndex 2), so scrolling rows can't
                        // paint over the pinned totals row.
                        '& .MuiTableCell-root': {
                          fontWeight: 700,
                          backgroundColor: theme.palette.background.paper,
                          backgroundImage: `linear-gradient(${tint}, ${tint})`,
                        },
                        position: 'sticky',
                        top: 56,
                        zIndex: 3,
                      };
                    }}
                  >
                    {renderDataCells(totalsRow, { isTotal: true })}
                  </TableRow>
                ) : null}

                {/* Skeleton loading */}
                {isLoading && rows.length === 0 ? (
                  Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      {COLUMNS.filter((c) => isCol(c.id)).map((col) => (
                        <TableCell key={col.id}>
                          <Skeleton variant="text" width={col.id === 'client' ? 140 : 60} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : null}

                {/* Data rows — parent-company groups + standalone clients */}
                {!isLoading || rows.length > 0 ? groupedEntries.map((entry) => {
                  if (entry.type === 'client') {
                    return (
                      <TableRow hover key={entry.key}>
                        {renderDataCells(entry.row)}
                      </TableRow>
                    );
                  }

                  const isExpanded = hasActiveRowFilter || expandedParents.has(entry.key);
                  return (
                    <React.Fragment key={entry.key}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => toggleParentExpanded(entry.key)}
                      >
                        {renderDataCells(entry.row, {
                          parentGroup: {
                            key: entry.key,
                            name: entry.parentName,
                            childCount: entry.children.length,
                            childrenRows: entry.children,
                            expanded: isExpanded,
                            dashboardPath: `/clients/${normalizeSlug(entry.parentName)}/dashboard`,
                          },
                        })}
                      </TableRow>
                      {isExpanded && entry.children.map((child) => (
                        <TableRow
                          key={child.uuid || `child-${child.client_id}`}
                          hover
                          sx={(theme) => ({
                            backgroundColor: safeAlpha(theme.palette.action.hover, 0.04),
                            '& .MuiTableCell-root': { fontSize: '0.85rem' },
                          })}
                        >
                          {renderDataCells(child, { indent: 1 })}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                }) : null}

                {!isLoading && groupedEntries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={visibleColCount} align="center">
                      {hasActiveRowFilter ? 'No clients match the current filters.' : 'No data found.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" mt={1}>
            <Typography variant="caption" color="text.secondary">
              {visibleClientCount} client{visibleClientCount !== 1 ? 's' : ''}
              {hasActiveRowFilter && baseClientRows.length !== visibleClientCount
                ? ` of ${baseClientRows.length}`
                : ''}
              {' · '}
              {(() => {
                const groupCount = groupedEntries.filter((entry) => entry.type === 'parent').length;
                return `${groupCount} parent group${groupCount !== 1 ? 's' : ''}`;
              })()}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
const getHoverBackground = (theme, opacity = 0.08) => {
  const hoverSource =
    theme?.palette?.primary?.main ||
    theme?.palette?.primary?.light ||
    theme?.palette?.secondary?.main ||
    theme?.palette?.text?.primary ||
    '#000000';
  return safeAlpha(hoverSource, opacity);
};
